import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const ServiceCard = ({ icon, title, description, features, price, isPopular, serviceType }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className={`relative bg-white rounded-2xl shadow-xl p-8 ${
      isPopular ? 'border-2 border-indigo-600 scale-105' : ''
    }`}
  >
    {isPopular && (
      <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium">
        Recommandé
      </span>
    )}
    <div className="space-y-6">
      {/* Icône */}
      <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center">
        {icon}
      </div>

      {/* Titre et Description */}
      <div>
        <h3 className="text-2xl font-bold text-indigo-900">{title}</h3>
        <p className="mt-2 text-gray-600">{description}</p>
      </div>

      {/* Prix */}
      <div className="flex items-baseline">
        <span className="text-4xl font-bold text-indigo-600">{price}</span>
      </div>

      {/* Caractéristiques */}
      <ul className="space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <svg className="w-5 h-5 text-indigo-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>

      {/* Bouton */}
      <Link
        to="/contact"
        state={{ service: serviceType }}
        className={`block text-center py-3 px-6 rounded-lg transition-colors duration-300 ${
          isPopular
            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
            : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
        }`}
      >
        En savoir plus
      </Link>
    </div>
  </motion.div>
);

const Services = () => {
  const { t } = useContext(LanguageContext);
  
  const services = [
    {
      serviceType: 'web',
      icon: (
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: t('webDevTitle'),
      description: t('webDevDesc'),
      features: [
        "Sites web responsive et applications modernes",
        "Optimisation des performances et du temps de chargement",
        "Intégration CMS pour une gestion de contenu simplifiée",
        "Architecture évolutive et maintenable",
        "Solutions personnalisées selon vos besoins"
      ]
    },
    {
      serviceType: 'seo',
      icon: (
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      title: t('seoTitle'),
      description: t('seoDesc'),
      features: [
        "Audit SEO approfondi et analyse de la concurrence",
        "Optimisation technique et structurelle de votre site",
        "Stratégie de contenu et mots-clés ciblés",
        "Suivi des performances et analyses détaillées",
        "Recommandations d'amélioration continues"
      ],
      isPopular: true
    },
    {
      serviceType: 'maintenance',
      icon: (
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: t('maintenanceTitle'),
      description: t('maintenanceDesc'),
      features: [
        "Mises à jour proactives des systèmes et plugins",
        "Sauvegardes automatisées et sécurisées",
        "Monitoring continu et alertes en temps réel",
        "Support technique personnalisé et réactif",
        "Sécurité renforcée et protection contre les menaces"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-36 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-indigo-900 mb-6"
          >
            {t('servicesTitle')}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl text-gray-600"
          >
            {t('servicesIntro')}
          </motion.p>
        </div>

        {/* Grille de services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>

        {/* Section Contact */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-20 text-center"
        >
          <p className="text-xl text-gray-600 mb-8">
            {t('needCustom')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
          >
            {t('discussProject')}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Services; 